import { Link } from '@tanstack/react-router'
import { ArrowLeft, ClipboardList, FileDown } from 'lucide-react'
import { BalansBalk } from '@/components/ui/BalansBalk'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { PageHeader } from '@/components/ui/PageHeader'
import { Skeleton } from '@/components/ui/Skeleton'
import { StatusDot } from '@/components/ui/StatusDot'
import { Verdeelbalk } from '@/components/ui/Verdeelbalk'
import { formatUren, formatUrenMetTeken, formatWeken } from '@/lib/format'
import { groepeerPerSoort, SOORT_KLEUR, soorturen, verdeling } from '@/lib/inzet'
import { useMedewerker } from '@/lib/medewerker-api'
import type { Inzetregel, MedewerkerInzet } from '@/lib/medewerker-types'
import { useSemesterKeuze } from '@/lib/semester-context'

export function MedewerkerPage({ medewerkerId }: { medewerkerId: string }) {
  const { semester, semesterId, isLoading: semesterLaadt } = useSemesterKeuze()
  const query = useMedewerker(semesterId, medewerkerId)
  const laadt = semesterLaadt || query.isLoading
  const detail = query.data

  return (
    <div className="space-y-6">
      <PageHeader
        title={detail?.naam ?? 'Takenplaat'}
        description={detail ? beschrijving(detail, semester?.naam) : null}
        actions={
          <>
            <a
              href={`/api/medewerkers/${medewerkerId}/takenplaat.pdf${semesterId ? `?semester=${semesterId}` : ''}`}
              target="_blank"
              rel="noreferrer"
              className="btn-secondary"
            >
              <FileDown className="h-4 w-4" />
              Takenplaat als pdf
            </a>
            <Link to="/" className="btn-ghost">
              <ArrowLeft className="h-4 w-4" />
              Terug naar team
            </Link>
          </>
        }
      />

      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : laadt || !detail ? (
        <MedewerkerSkelet />
      ) : (
        <Takenplaat inzet={detail.inzet} />
      )}
    </div>
  )
}

function beschrijving(
  detail: { email: string | null; aanstellingFte: number | null },
  semesterNaam?: string,
) {
  const delen = [
    detail.email,
    detail.aanstellingFte !== null
      ? `aanstelling ${detail.aanstellingFte.toLocaleString('nl-NL')} fte`
      : null,
    semesterNaam ?? null,
  ].filter((deel): deel is string => Boolean(deel))

  return delen.length > 0 ? delen.join(' \u00b7 ') : null
}

function Takenplaat({ inzet }: { inzet: MedewerkerInzet }) {
  const secties = groepeerPerSoort(inzet.regels)
  const vakken = verdeling(soorturen(inzet))

  return (
    <div className="space-y-6">
      <div className="space-y-4 rounded-card bg-surface px-5 py-4 shadow-card">
        <div className="flex items-center gap-2">
          <StatusDot status={inzet.status} label />
        </div>
        <BalansBalk
          toegewezen={inzet.totaal}
          beschikbaar={inzet.beschikbaar}
          status={inzet.status}
        />
        <Verdeelbalk vakken={vakken} className="border-t border-hairline pt-4" />
      </div>

      {secties.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title="Nog geen taken toegewezen"
          description="Zodra er lessen of taken aan deze medewerker hangen, staan ze hier per soort bij elkaar."
        />
      ) : (
        <div className="overflow-hidden rounded-card bg-surface shadow-card">
          {secties.map((sectie) => (
            <section key={sectie.soort}>
              <div className="flex items-baseline justify-between gap-4 border-b border-hairline bg-fill-1 px-5 py-2.5">
                <h2 className="label-caps flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className={`h-2.5 w-2.5 rounded-[2px] ${SOORT_KLEUR[sectie.soort].vlak}`}
                  />
                  {sectie.label}
                </h2>
                <span className="num text-xs text-ink-faint">{formatUren(sectie.uren)} u</span>
              </div>
              <ul className="divide-y divide-hairline">
                {sectie.regels.map((regel, index) => (
                  <TaakRegel key={`${sectie.soort}-${index}`} regel={regel} />
                ))}
              </ul>
            </section>
          ))}

          <Totaalblok inzet={inzet} />
        </div>
      )}
    </div>
  )
}

function TaakRegel({ regel }: { regel: Inzetregel }) {
  const context = [regel.groepCode, regel.vak].filter(Boolean).join(' · ')

  return (
    <li className="grid grid-cols-[minmax(0,1fr)_4.5rem] items-baseline gap-3 px-5 py-3 sm:grid-cols-[minmax(0,1fr)_6rem_5rem]">
      <div className="min-w-0">
        <p className="truncate text-sm text-ink">{regel.omschrijving}</p>
        {context ? <p className="mt-0.5 text-xs text-ink-faint">{context}</p> : null}
        {regel.toelichting ? (
          <p className="mt-0.5 text-xs text-ink-faint">{regel.toelichting}</p>
        ) : null}
      </div>
      <span className="num hidden text-sm text-ink-faint sm:block">{formatWeken(regel.weken)}</span>
      <span className="num text-sm text-ink">{formatUren(regel.uren)} u</span>
    </li>
  )
}

function Totaalblok({ inzet }: { inzet: MedewerkerInzet }) {
  const regels = [
    { label: 'Toegewezen', waarde: `${formatUren(inzet.totaal)} u`, nadruk: true },
    {
      label: 'Beschikbaar',
      waarde: inzet.beschikbaar === null ? 'onbekend' : `${formatUren(inzet.beschikbaar)} u`,
      nadruk: false,
    },
    {
      label: 'Ruimte',
      waarde: inzet.balans === null ? '-' : `${formatUrenMetTeken(inzet.balans)} u`,
      nadruk: false,
    },
  ]

  return (
    <div className="border-t border-hairline-strong px-5 py-3">
      <dl className="space-y-1.5">
        {regels.map((regel) => (
          <div key={regel.label} className="grid grid-cols-[minmax(0,1fr)_6rem] items-baseline gap-3">
            <dt className={regel.nadruk ? 'text-sm font-medium text-ink' : 'text-sm text-ink-soft'}>
              {regel.label}
            </dt>
            <dd
              className={
                regel.nadruk ? 'num text-sm font-semibold text-ink' : 'num text-sm text-ink-soft'
              }
            >
              {regel.waarde}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function MedewerkerSkelet() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-[92px] w-full rounded-card" />
      <div className="overflow-hidden rounded-card bg-surface shadow-card">
        <div className="divide-y divide-hairline">
          {Array.from({ length: 7 }).map((_, index) => (
            <div key={index} className="flex items-center justify-between gap-4 px-5 py-4">
              <Skeleton className="h-4 w-56" />
              <Skeleton className="h-4 w-12" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
